"use client";

/**
 * The product mocks used across the marketing pages.
 *
 * These are drawn from the real screens rather than photographed, for one practical reason:
 * a screenshot of a product with 20 members shows a product with 20 members. They use the
 * real components' shapes, spacing, radii and colours, and every number in them is either a
 * real limit from lib/data/subscriptions.ts or clearly an example.
 *
 * Each page gets a DIFFERENT one. Five pages showing the same card is how a site starts to
 * feel generated, and the point of a feature page is to show that feature.
 */

import {
  BadgeCheck,
  CalendarDays,
  Check,
  Compass,
  Handshake,
  MapPin,
  MessageSquare,
  Rocket,
  Star,
  Ticket,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import { MockFrame } from "@/components/marketing/site-ui";
import { useSiteContent } from "@/components/marketing/use-site-content";
import { cn } from "@/lib/utils";

/** Discovery: the match score with its reasons written underneath. The signature screen. */
export function MatchScoreMock() {
  const c = useSiteContent();
  const m = c.mocks;

  return (
    <MockFrame label={m.exampleLabel}>
      <div className="flex items-start gap-4">
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl gradient-primary text-white">
          <Compass className="h-6 w-6" aria-hidden />
          <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-white">
            <BadgeCheck className="h-4 w-4 text-primary" aria-hidden />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold leading-tight text-neutral-900">{m.match.role}</p>
          <p className="mt-0.5 text-sm text-neutral-600">{m.match.meta}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-3xl font-extrabold leading-none tracking-[-0.03em] text-primary">92</p>
          <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-primary">{m.match.label}</p>
        </div>
      </div>

      <div className="mt-4 space-y-2 rounded-2xl bg-primary-lighter p-3.5">
        <p className="gs-eyebrow text-primary">{m.match.whyHeading}</p>
        {m.match.reasons.map((reason) => (
          <p key={reason} className="flex items-start gap-2 text-[13.5px] leading-snug text-neutral-700">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={3} aria-hidden />
            {reason}
          </p>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <span className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-neutral-200 py-2.5 text-sm font-semibold text-neutral-600">
          <X className="h-4 w-4" aria-hidden />
          {m.match.pass}
        </span>
        <span className="flex flex-[1.4] items-center justify-center gap-2 rounded-xl gradient-primary py-2.5 text-sm font-bold text-white">
          {m.match.connect}
        </span>
      </div>

      <p className="mt-3 text-center text-[12.5px] leading-snug text-neutral-600">{m.match.consentNote}</p>
    </MockFrame>
  );
}

/** Events: how an event is found, which is the actual argument on that page. */
export function EventDiscoveryMock() {
  const c = useSiteContent();
  const m = c.mocks;

  return (
    <MockFrame label={m.exampleLabel}>
      <div className="flex flex-wrap gap-2">
        {m.events.filters.map((filter, index) => (
          <span
            key={filter}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
              index === 0 ? "gradient-primary text-white" : "bg-primary/8 text-primary",
            )}
          >
            {filter}
          </span>
        ))}
      </div>

      <div className="mt-4 space-y-2.5">
        {m.events.list.map((event, index) => (
          <div
            key={event.title}
            className={cn(
              "flex items-start gap-3 rounded-2xl border p-3",
              index === 0 ? "border-primary/30 bg-primary-lighter/60" : "border-neutral-200",
            )}
          >
            <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl gradient-primary text-white">
              <span className="text-[9px] font-bold uppercase tracking-wider opacity-90">{event.month}</span>
              <span className="text-lg font-extrabold leading-none">{event.day}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-neutral-900">{event.title}</p>
              <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-neutral-600">
                <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                {event.place}
              </p>
              <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-primary">
                <Ticket className="h-3 w-3 shrink-0" aria-hidden />
                {event.price}
              </p>
            </div>
            <span className="flex shrink-0 items-center gap-1 rounded-lg bg-neutral-100 px-2 py-1 text-[11px] font-semibold text-neutral-600">
              <Users className="h-3 w-3" aria-hidden />
              {event.going}
            </span>
          </div>
        ))}
      </div>

      <p className="mt-3.5 text-center text-[12.5px] leading-snug text-neutral-600">{m.events.note}</p>
    </MockFrame>
  );
}

/** Communities: a real question with the answer that saves someone a month. */
export function CommunityThreadMock() {
  const c = useSiteContent();
  const m = c.mocks;

  return (
    <MockFrame label={m.exampleLabel}>
      <div className="flex items-center justify-between gap-3 border-b border-neutral-100 pb-3">
        <span className="flex items-center gap-2 text-sm font-bold text-neutral-900">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Users className="h-4 w-4" aria-hidden />
          </span>
          {m.community.name}
        </span>
        <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
          {m.community.memberCount}
        </span>
      </div>

      <div className="pt-4">
        <p className="text-[15px] font-bold leading-snug text-neutral-900">{m.community.question}</p>
        <p className="mt-1 text-xs text-neutral-600">{m.community.askedBy}</p>
      </div>

      <div className="mt-4 space-y-2.5">
        {m.community.replies.map((reply, index) => (
          <div key={reply.body} className="rounded-2xl border border-neutral-200 p-3">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-lg text-white",
                  index === 0 ? "gradient-primary" : "bg-neutral-800",
                )}
              >
                <span className="text-[10px] font-bold">{reply.initials}</span>
              </span>
              <span className="text-xs font-bold text-neutral-800">{reply.role}</span>
            </div>
            <p className="mt-2 text-[13.5px] leading-relaxed text-neutral-600">{reply.body}</p>
          </div>
        ))}
      </div>

      <p className="mt-3.5 flex items-center justify-center gap-1.5 text-[12.5px] text-neutral-600">
        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
        {m.community.note}
      </p>
    </MockFrame>
  );
}

/** Opportunities: a specific ask, with applicants the poster picks between. */
export function OpportunityMock() {
  const c = useSiteContent();
  const m = c.mocks;

  return (
    <MockFrame label={m.exampleLabel}>
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl gradient-primary text-white">
          <Handshake className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold leading-snug text-neutral-900">{m.opportunity.title}</p>
          <p className="mt-1 text-xs text-neutral-600">{m.opportunity.meta}</p>
        </div>
      </div>

      <p className="mt-3 text-[13.5px] leading-relaxed text-neutral-600">{m.opportunity.body}</p>

      <div className="mt-4 rounded-2xl bg-neutral-50 p-3">
        <p className="gs-eyebrow text-neutral-600">{m.opportunity.applicantsHeading}</p>
        <div className="mt-2.5 space-y-2">
          {m.opportunity.applicants.map((applicant, index) => (
            <div key={applicant.role} className="flex items-center gap-2.5 rounded-xl bg-white p-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[10px] font-bold text-primary">
                {applicant.initials}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-neutral-900">{applicant.role}</p>
                <p className="truncate text-[11px] text-neutral-600">{applicant.meta}</p>
              </div>
              {index === 0 ? (
                <span className="shrink-0 rounded-lg gradient-primary px-2.5 py-1 text-[11px] font-bold text-white">
                  {m.opportunity.accept}
                </span>
              ) : (
                <span className="shrink-0 rounded-lg border border-neutral-200 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
                  {m.opportunity.decline}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      <p className="mt-3.5 text-center text-[12.5px] leading-snug text-neutral-600">{m.opportunity.note}</p>
    </MockFrame>
  );
}

/** Reputation: the profile score with the gaps named, plus the evidence behind it. */
export function ProfileScoreMock() {
  const c = useSiteContent();
  const m = c.mocks;

  return (
    <MockFrame label={m.exampleLabel}>
      <div className="flex items-center gap-4">
        <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full">
          <svg viewBox="0 0 36 36" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3.2" />
            <circle
              cx="18"
              cy="18"
              r="15.9"
              fill="none"
              stroke="url(#gsScore)"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeDasharray="78 100"
            />
            <defs>
              <linearGradient id="gsScore" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#217af6" />
                <stop offset="100%" stopColor="#4a5efd" />
              </linearGradient>
            </defs>
          </svg>
          <span className="relative text-xl font-extrabold tracking-[-0.03em] text-neutral-900">78</span>
        </div>
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-neutral-900">{m.profile.heading}</p>
          <p className="mt-1 text-[13px] leading-snug text-neutral-600">{m.profile.body}</p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {m.profile.gaps.map((gap) => (
          <div key={gap.label} className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 px-3 py-2.5">
            <span className="min-w-0 truncate text-[13px] font-semibold text-neutral-700">{gap.label}</span>
            <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">{gap.points}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          { icon: Star, value: m.profile.stats.reviews, label: m.profile.stats.reviewsLabel },
          { icon: CalendarDays, value: m.profile.stats.meetings, label: m.profile.stats.meetingsLabel },
          { icon: TrendingUp, value: m.profile.stats.views, label: m.profile.stats.viewsLabel },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="rounded-xl bg-neutral-50 p-2.5 text-center">
              <Icon className="mx-auto h-4 w-4 text-primary" aria-hidden />
              <p className="mt-1 text-sm font-extrabold text-neutral-900">{stat.value}</p>
              <p className="text-[10.5px] leading-tight text-neutral-600">{stat.label}</p>
            </div>
          );
        })}
      </div>
    </MockFrame>
  );
}

/** A compact "who is here" panel used on the audience pages. */
export function AudienceMock({ variant }: { variant: "founders" | "owners" }) {
  const c = useSiteContent();
  const m = c.mocks;
  const rows = m.audience[variant];

  return (
    <MockFrame label={m.exampleLabel}>
      <div className="space-y-2.5">
        {rows.map((row, index) => (
          <div
            key={row.role}
            className={cn(
              "flex items-center gap-3 rounded-2xl border p-3",
              index === 0 ? "border-primary/30 bg-primary-lighter/60" : "border-neutral-200",
            )}
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl gradient-primary text-white">
              <Rocket className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-neutral-900">{row.role}</p>
              <p className="truncate text-xs text-neutral-600">{row.meta}</p>
            </div>
            <span className="shrink-0 text-right">
              <span className="block text-base font-extrabold leading-none text-primary">{row.score}</span>
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-600">{m.audience.fit}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3.5 text-center text-[12.5px] leading-snug text-neutral-600">{m.audience.note}</p>
    </MockFrame>
  );
}
